import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, RotateCcw, Send, Save } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { WaTestSend } from "@/components/whatsapp/WaTestSend";

type Cfg = {
    events: { code: string; placeholders: string[]; urgent: boolean }[];
    languages: string[];
    channels: Record<string, { connected: boolean }>;
    settings: { events: Record<string, Record<string, boolean>>; reminders: { before_days: number; every_days: number; max_overdue: number } };
    enabled: boolean;
};

export default function NotificationSettings() {
    const { t } = useTranslation();
    const { hasPerm, hasModuleAccess } = useAuth();
    const canEdit = hasPerm("institution.edit");
    const [raw, setCfg] = useState<Cfg | null>(null);
    const [saving, setSaving] = useState(false);
    const kgOn = hasModuleAccess("kindergarten", "view");
    // Kindergarten events (kg_*) only show when the tenant has the kindergarten module.
    const cfg = useMemo(() => raw && { ...raw, events: raw.events.filter((e) => kgOn || !e.code.startsWith("kg_")) }, [raw, kgOn]);

    const load = useCallback(() => api.get("/notification-center/config").then(r => setCfg(r.data)).catch(e => toast.error(formatApiError(e))), []);
    useEffect(() => { load(); }, [load]);
    if (!cfg) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;

    const toggle = async (ev: string, ch: string, on: boolean) => {
        try {
            const r = await api.put("/notification-center/settings", { events: { [ev]: { [ch]: on } } });
            setCfg({ ...raw!, settings: r.data });
        } catch (e: any) { toast.error(formatApiError(e)); }
    };
    const saveReminders = async () => {
        setSaving(true);
        try {
            const r = await api.put("/notification-center/settings", { reminders: cfg.settings.reminders });
            setCfg({ ...raw!, settings: r.data }); toast.success(t("notif.settings.saved"));
        } catch (e: any) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };
    const rem = cfg.settings.reminders;
    const setRem = (k: keyof typeof rem, v: string) => setCfg({ ...raw!, settings: { ...cfg.settings, reminders: { ...rem, [k]: Math.max(0, parseInt(v || "0", 10)) } } });
    const waOff = !cfg.channels.whatsapp?.connected;

    return (
        <div className="space-y-6" data-testid="notification-settings-page">
            <div>
                <h1 className="text-2xl font-semibold" style={{ color: "var(--text)" }}>{t("notif.settings.title")}</h1>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("notif.settings.subtitle")}</p>
                {!cfg.enabled && <div className="mt-3 text-sm rounded-md p-3" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="notif-disabled-banner">{t("notif.settings.disabled_banner")}</div>}
            </div>
            <div className="surface-card overflow-x-auto">
                <table className="w-full text-sm" data-testid="notif-events-table">
                    <thead><tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                        <th className="px-4 py-3 font-medium">{t("notif.col.event")}</th>
                        <th className="px-4 py-3 font-medium w-32">Email</th>
                        <th className="px-4 py-3 font-medium w-48">WhatsApp</th>
                    </tr></thead>
                    <tbody>
                        {cfg.events.map(ev => (
                            <tr key={ev.code} className="border-t" style={{ borderColor: "var(--border)" }}>
                                <td className="px-4 py-3" style={{ color: "var(--text)" }}>{t(`notif.event.${ev.code}`)}{ev.urgent && <span className="ml-2 text-xs" style={{ color: "var(--error-fg)" }}>{t("notif.settings.urgent")}</span>}</td>
                                <td className="px-4 py-3"><input type="checkbox" className="h-4 w-4" data-testid={`notif-toggle-${ev.code}-email`} disabled={!canEdit}
                                    checked={!!cfg.settings.events[ev.code]?.email} onChange={e => toggle(ev.code, "email", e.target.checked)} /></td>
                                <td className="px-4 py-3">
                                    <label className="inline-flex items-center gap-2" title={waOff ? t("notif.settings.wa_off") : undefined}>
                                        <input type="checkbox" className="h-4 w-4" data-testid={`notif-toggle-${ev.code}-whatsapp`} disabled={!canEdit || waOff}
                                            checked={!!cfg.settings.events[ev.code]?.whatsapp} onChange={e => toggle(ev.code, "whatsapp", e.target.checked)} />
                                        {waOff && <span className="text-xs" style={{ color: "var(--text-muted)" }}>{t("notif.settings.wa_off")}</span>}
                                    </label>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="surface-card p-5 space-y-3" data-testid="notif-reminders">
                <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>{t("notif.reminders.title")}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {([["before_days", "notif.reminders.before"], ["every_days", "notif.reminders.every"], ["max_overdue", "notif.reminders.max"]] as const).map(([k, label]) => (
                        <label key={k} className="block text-sm">
                            <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t(label)}</div>
                            <input type="number" min={k === "every_days" ? 1 : 0} max={60} value={rem[k]} disabled={!canEdit}
                                data-testid={`notif-rem-${k}`} onChange={e => setRem(k, e.target.value)} className="surface-input w-full px-3 py-2 text-sm" />
                        </label>
                    ))}
                </div>
                {canEdit && <button onClick={saveReminders} disabled={saving} data-testid="notif-rem-save"
                    className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t("common.save")}</button>}
            </div>
            <TemplateEditor key={cfg.events.map((e) => e.code).join(",")} cfg={cfg} canEdit={canEdit} />
        </div>
    );
}

function TemplateEditor({ cfg, canEdit }: { cfg: Cfg; canEdit: boolean }) {
    const { t } = useTranslation();
    const [ev, setEv] = useState(cfg.events[0]?.code || "");
    const [lang, setLang] = useState("az");
    const [tpl, setTpl] = useState<{ subject: string; body: string; is_default: boolean; placeholders: string[]; whatsapp_text?: string } | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const { me } = useAuth();
    const testTo = me?.user?.email || "";  // SEC-003: test send goes only to the caller's own login email

    useEffect(() => {
        setErr(null);
        api.get(`/notification-center/templates/${ev}/${lang}`).then(r => setTpl(r.data)).catch(e => toast.error(formatApiError(e)));
    }, [ev, lang]);

    const unknown = (text: string) => {
        const allowed = new Set(tpl?.placeholders || []);
        return Array.from(new Set(Array.from(text.matchAll(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g)).map(m => m[1]))).filter(p => !allowed.has(p));
    };
    const run = async (fn: () => Promise<any>, ok?: string) => {
        setBusy(true); setErr(null);
        try { await fn(); if (ok) toast.success(ok); }
        catch (e: any) {
            const d = e?.response?.data?.detail;
            if (d?.error === "unknown_placeholder") setErr(t("notif.tpl.unknown", { names: d.placeholders.map((p: string) => `{${p}}`).join(", ") }));
            else toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };
    const save = () => {
        if (!tpl) return;
        const bad = unknown(tpl.subject + " " + tpl.body);
        if (bad.length) { setErr(t("notif.tpl.unknown", { names: bad.map(p => `{${p}}`).join(", ") })); return; }
        run(async () => setTpl((await api.put(`/notification-center/templates/${ev}/${lang}`, { subject: tpl.subject, body: tpl.body })).data), t("notif.tpl.saved"));
    };
    const reset = () => run(async () => setTpl((await api.delete(`/notification-center/templates/${ev}/${lang}`)).data), t("notif.tpl.reset_done"));
    const test = () => run(async () => { await api.post(`/notification-center/templates/${ev}/${lang}/test`, { subject: tpl?.subject, body: tpl?.body }); }, t("notif.tpl.test_sent", { to: testTo }));

    return (
        <div className="surface-card p-5 space-y-4" data-testid="notif-template-editor">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-base font-semibold flex-1" style={{ color: "var(--text)" }}>{t("notif.tpl.title")}</h2>
                <select value={ev} onChange={e => setEv(e.target.value)} className="surface-input px-3 py-2 text-sm" data-testid="notif-tpl-event">
                    {cfg.events.map(e => <option key={e.code} value={e.code}>{t(`notif.event.${e.code}`)}</option>)}
                </select>
                <div className="inline-flex rounded-md border overflow-hidden" style={{ borderColor: "var(--border)" }}>
                    {cfg.languages.map(l => (
                        <button key={l} onClick={() => setLang(l)} data-testid={`notif-tpl-lang-${l}`} className="px-3 py-1.5 text-xs font-medium uppercase"
                            style={{ background: lang === l ? "var(--selected-bg)" : "transparent", color: lang === l ? "var(--text)" : "var(--text-muted)" }}>{l}</button>
                    ))}
                </div>
            </div>
            {tpl && (
                <>
                    <label className="block">
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("notif.tpl.subject")}</div>
                        <input value={tpl.subject} disabled={!canEdit} onChange={e => setTpl({ ...tpl, subject: e.target.value })} data-testid="notif-tpl-subject" className="surface-input w-full px-3 py-2 text-sm" />
                    </label>
                    <label className="block">
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("notif.tpl.body")}</div>
                        <textarea rows={7} value={tpl.body} disabled={!canEdit} onChange={e => setTpl({ ...tpl, body: e.target.value })} data-testid="notif-tpl-body" className="surface-input w-full px-3 py-2 text-sm font-mono" />
                    </label>
                    <div className="text-xs flex flex-wrap gap-1.5" style={{ color: "var(--text-muted)" }} data-testid="notif-tpl-placeholders">
                        {t("notif.tpl.placeholders")}: {tpl.placeholders.map(p => <code key={p} className="px-1.5 py-0.5 rounded" style={{ background: "var(--input)" }}>{`{${p}}`}</code>)}
                    </div>
                    {tpl.whatsapp_text && (
                        <div className="rounded-md border p-3 space-y-1" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid="notif-wa-readonly">
                            <div className="text-xs font-medium" style={{ color: "var(--text)" }}>{t("notif.wa.readonly_title")}</div>
                            <pre className="text-xs whitespace-pre-wrap font-mono" style={{ color: "var(--text-muted)" }} data-testid="notif-wa-text">{tpl.whatsapp_text}</pre>
                            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{t("notif.wa.readonly_hint", { n: "{{n}}" })}</div>
                        </div>
                    )}
                    {err && <div className="text-sm" style={{ color: "var(--error-fg)" }} data-testid="notif-tpl-error">{err}</div>}
                    {canEdit && (
                        <div className="flex flex-wrap items-center gap-2">
                            <button onClick={save} disabled={busy} data-testid="notif-tpl-save" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}><Save size={14} /> {t("common.save")}</button>
                            <button onClick={reset} disabled={busy || tpl.is_default} data-testid="notif-tpl-reset" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm border disabled:opacity-50" style={{ borderColor: "var(--border)", color: "var(--text)" }}><RotateCcw size={14} /> {t("notif.tpl.reset")}</button>
                            <span className="flex-1" />
                            <span className="text-xs truncate max-w-[16rem]" style={{ color: "var(--text-muted)" }} data-testid="notif-tpl-test-to">{t("notif.tpl.test_to_self", { to: testTo })}</span>
                            <button onClick={test} disabled={busy || !testTo.includes("@")} data-testid="notif-tpl-test" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm border disabled:opacity-50" style={{ borderColor: "var(--border)", color: "var(--text)" }}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t("notif.tpl.test")}</button>
                            <WaTestSend text={tpl.whatsapp_text || tpl.body} templateKey={ev} disabled={busy} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
